import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FiCheck, FiLock, FiCreditCard, FiPackage, FiAlertCircle, FiEye, FiEyeOff, FiSave, FiExternalLink, FiMapPin, FiPlus, FiTrash2 } from 'react-icons/fi';
import { dealerOnboardingService } from '../services/dealerOnboardingService';
import { stripeConnectService } from '../services/stripeConnectService';
import { supabase } from '../lib/supabase';

const DealerOnboard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [publishableKey, setPublishableKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [savingKeys, setSavingKeys] = useState(false);
  const [stripeMessage, setStripeMessage] = useState('');
  const [lotLocations, setLotLocations] = useState([]);
  const [savingLocations, setSavingLocations] = useState(false);

  const dealerPlan = invitation?.dealers?.subscription_plan || 'basic';
  const hasMultiLocation = dealerPlan === 'pro' || dealerPlan === 'enterprise';

  // Step mapping: basic = 1,2,3,4 | pro/enterprise = 1,2,3,4,5
  const STEP_VERIFY = 1;
  const STEP_PASSWORD = 2;
  const STEP_LOCATIONS = 3; // only for pro/enterprise
  const STEP_STRIPE = hasMultiLocation ? 4 : 3;
  const STEP_DONE = hasMultiLocation ? 5 : 4;

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setError('No invitation token provided.');
      setLoading(false);
    }
  }, [token]);

  const validateToken = async () => {
    setLoading(true);
    const result = await dealerOnboardingService.validateInvite(token);

    if (result.valid) {
      setInvitation(result.data);
      setStep(2);
    } else {
      setError(result.error || 'Invalid invitation');
      if (result.data?.status === 'accepted') {
        setStep(4);
      }
    }
    setLoading(false);
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    const result = await dealerOnboardingService.acceptInvite(token, password);

    if (result.error) {
      setError(typeof result.error === 'string' ? result.error : result.error.message || 'Account creation failed');
      setSubmitting(false);
      return;
    }

    if (hasMultiLocation) {
      setLotLocations(invitation?.dealers?.lot_locations || []);
      setStep(STEP_LOCATIONS);
    } else {
      setStep(STEP_STRIPE);
    }
    setSubmitting(false);
  };

  const addLotLocation = () => {
    setLotLocations(prev => [
      ...prev,
      { id: crypto.randomUUID(), name: '', address: '', city: '', state: '', zip: '' }
    ]);
  };

  const updateLotLocation = (id, field, value) => {
    setLotLocations(prev => prev.map(loc =>
      loc.id === id ? { ...loc, [field]: value } : loc
    ));
  };

  const removeLotLocation = (id) => {
    setLotLocations(prev => prev.filter(loc => loc.id !== id));
  };

  const handleSaveLocations = async () => {
    const validLocations = lotLocations.filter(loc => loc.name.trim());
    if (validLocations.length === 0) {
      setError('Please add at least one lot location with a name.');
      return;
    }
    setSavingLocations(true);
    setError('');
    try {
      const dealerId = invitation?.dealer_id;
      if (dealerId) {
        await supabase.from('dealers').update({ lot_locations: validLocations }).eq('id', dealerId);
      }
      setStep(STEP_STRIPE);
    } catch (err) {
      console.error('Save locations error:', err);
      setError('Failed to save locations. Please try again.');
    } finally {
      setSavingLocations(false);
    }
  };

  const handleSkipLocations = () => {
    setStep(STEP_STRIPE);
  };

  const handleSaveStripeKeys = async () => {
    const dealerId = invitation?.dealer_id;
    if (!dealerId) return;

    if (!publishableKey.startsWith('pk_')) {
      setStripeMessage('Publishable key must start with pk_');
      return;
    }
    if (!secretKey.startsWith('sk_')) {
      setStripeMessage('Secret key must start with sk_');
      return;
    }

    setSavingKeys(true);
    setStripeMessage('');

    const result = await stripeConnectService.saveStripeKeys(dealerId, publishableKey, secretKey);
    if (result.error) {
      setStripeMessage('Failed to save keys: ' + (result.error.message || 'Unknown error'));
    } else {
      setStep(STEP_DONE);
    }
    setSavingKeys(false);
  };

  const handleSkipStripe = () => {
    setStep(STEP_DONE);
  };

  const steps = hasMultiLocation
    ? [
        { num: 1, label: 'Verify Invite' },
        { num: 2, label: 'Create Account' },
        { num: 3, label: 'Lot Locations' },
        { num: 4, label: 'Connect Stripe' },
        { num: 5, label: 'Ready' },
      ]
    : [
        { num: 1, label: 'Verify Invite' },
        { num: 2, label: 'Create Account' },
        { num: 3, label: 'Connect Stripe' },
        { num: 4, label: 'Ready' },
      ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4" />
          <p className="text-gray-600">Validating your invitation...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dealer Onboarding - ShedX</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-20 pb-12">
        <div className="max-w-xl mx-auto px-4">
          {/* Logo / Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">ShedX</h1>
            <p className="text-gray-500 mt-1">Dealer Onboarding</p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-10">
            {steps.map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    step > s.num
                      ? 'bg-emerald-600 text-white'
                      : step === s.num
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > s.num ? <FiCheck className="w-4 h-4" /> : s.num}
                  </div>
                  <span className="text-[11px] text-gray-500 mt-1.5 hidden sm:block">{s.label}</span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`w-12 sm:w-20 h-0.5 mx-1 ${step > s.num ? 'bg-emerald-600' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Error State (invalid/expired) */}
          {error && step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-200 rounded-xl p-8 text-center"
            >
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiAlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Invitation</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <p className="text-sm text-gray-500">
                Contact your ShedX admin to receive a new invitation link.
              </p>
            </motion.div>
          )}

          {/* Step 2: Set Password */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-200 rounded-xl p-8"
            >
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiLock className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Create Your Account</h2>
                <p className="text-gray-500 mt-1">
                  Welcome, <span className="font-medium text-gray-700">{invitation?.dealers?.company_name}</span>
                </p>
              </div>

              <form onSubmit={handleCreateAccount} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={invitation?.email || ''}
                    disabled
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Minimum 8 characters"
                      required
                      minLength={8}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    required
                    minLength={8}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <FiAlertCircle className="flex-shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gray-900 hover:bg-gray-800 disabled:bg-gray-400 text-white font-medium py-3.5 rounded-lg transition-colors"
                >
                  {submitting ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>
            </motion.div>
          )}

          {/* Step 3: Lot Locations (Pro/Enterprise only) */}
          {step === STEP_LOCATIONS && hasMultiLocation && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-200 rounded-xl p-8"
            >
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiMapPin className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Add Your Lot Locations</h2>
                <p className="text-gray-500 mt-1">
                  Add all your shed lot/yard locations so you can assign sheds to specific lots
                </p>
              </div>

              <div className="space-y-3 mb-4">
                {lotLocations.map((loc, idx) => (
                  <div key={loc.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500">Location #{idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeLotLocation(loc.id)}
                        className="text-red-400 hover:text-red-600 p-1 hover:bg-red-50 rounded transition-colors"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="sm:col-span-2">
                        <input
                          type="text"
                          placeholder="Location name (e.g., Main Lot, North Yard) *"
                          value={loc.name}
                          onChange={e => updateLotLocation(loc.id, 'name', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <input
                          type="text"
                          placeholder="Street address"
                          value={loc.address}
                          onChange={e => updateLotLocation(loc.id, 'address', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="City"
                          value={loc.city}
                          onChange={e => updateLotLocation(loc.id, 'city', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="State"
                          value={loc.state}
                          onChange={e => updateLotLocation(loc.id, 'state', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                        <input
                          type="text"
                          placeholder="ZIP"
                          value={loc.zip}
                          onChange={e => updateLotLocation(loc.id, 'zip', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addLotLocation}
                className="w-full border-2 border-dashed border-gray-300 hover:border-emerald-400 text-gray-500 hover:text-emerald-600 py-3 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm font-medium mb-4"
              >
                <FiPlus className="w-4 h-4" />
                Add Lot Location
              </button>

              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2 mb-4">
                  <FiAlertCircle className="flex-shrink-0" />
                  {error}
                </div>
              )}

              <button
                onClick={handleSaveLocations}
                disabled={savingLocations}
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-medium py-3.5 rounded-lg transition-colors"
              >
                {savingLocations ? 'Saving Locations...' : 'Save Locations & Continue'}
              </button>

              <button
                onClick={handleSkipLocations}
                className="w-full text-gray-500 hover:text-gray-700 text-sm py-2 mt-2 transition-colors"
              >
                Skip for now — I'll add locations later
              </button>
            </motion.div>
          )}

          {/* Stripe API Keys Step */}
          {step === STEP_STRIPE && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-200 rounded-xl p-8"
            >
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-[#635bff] rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiCreditCard className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Connect Payment Processing</h2>
                <p className="text-gray-500 mt-1">
                  Enter your Stripe API keys to receive payments from shed sales
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-gray-900 mb-2 text-sm">Where to find your keys</h3>
                <ol className="space-y-1.5 text-sm text-gray-600">
                  <li>1. Log in to <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-[#635bff] hover:underline">dashboard.stripe.com/apikeys</a></li>
                  <li>2. Copy your <strong>Publishable key</strong> (starts with pk_)</li>
                  <li>3. Copy your <strong>Secret key</strong> (starts with sk_)</li>
                  <li>4. Paste both below and save</li>
                </ol>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Publishable Key</label>
                  <input
                    type="text"
                    value={publishableKey}
                    onChange={(e) => setPublishableKey(e.target.value)}
                    placeholder="pk_live_... or pk_test_..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635bff] focus:border-[#635bff] font-mono text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Secret Key</label>
                  <div className="relative">
                    <input
                      type={showSecret ? 'text' : 'password'}
                      value={secretKey}
                      onChange={(e) => setSecretKey(e.target.value)}
                      placeholder="sk_live_... or sk_test_..."
                      className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635bff] focus:border-[#635bff] font-mono text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showSecret ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Stored securely. Never exposed to end users.</p>
                </div>

                {stripeMessage && (
                  <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <FiAlertCircle className="flex-shrink-0" />
                    {stripeMessage}
                  </div>
                )}

                <button
                  onClick={handleSaveStripeKeys}
                  disabled={savingKeys || !publishableKey || !secretKey}
                  className="w-full bg-[#635bff] hover:bg-[#5851db] disabled:bg-gray-300 text-white font-medium py-3.5 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <FiSave className="w-4 h-4" />
                  {savingKeys ? 'Saving...' : 'Save Keys & Continue'}
                </button>

                <button
                  onClick={handleSkipStripe}
                  className="w-full text-gray-500 hover:text-gray-700 text-sm py-2 transition-colors"
                >
                  Skip for now — I'll add keys later
                </button>
              </div>

              <p className="text-xs text-gray-400 text-center mt-4">
                You can update your Stripe keys anytime from your dealer dashboard settings.
              </p>
            </motion.div>
          )}

          {/* All Done Step */}
          {step === STEP_DONE && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-200 rounded-xl p-8 text-center"
            >
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiCheck className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">You're All Set!</h2>
              <p className="text-gray-500 mb-8">
                Your dealer account is ready. Log in to start managing your shed inventory.
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => navigate('/dealer/login')}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3.5 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <FiPackage className="w-5 h-5" />
                  Log In to Your Dashboard
                </button>
              </div>

              <div className="mt-8 bg-gray-50 rounded-lg p-4 text-left">
                <h3 className="font-medium text-gray-900 text-sm mb-2">Next steps</h3>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>1. Log in at <strong>homedepotshed.com/dealer/login</strong></li>
                  <li>2. Add your Stripe API keys in Settings</li>
                  <li>3. Upload your first shed listing with photos</li>
                  <li>4. Publish your sheds so buyers can see them</li>
                </ul>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
};

export default DealerOnboard;
